import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Button,
  Box,
  Stack,
  MenuItem,
  Autocomplete,
  Alert,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import type { ContractStatus, UpdateUserData } from '@/types'
import { useEnumsStore } from '@/stores/enumsStore'
import { displayErrorMessage, getErrorMessage, getValidationErrors } from '@/utils/error'

/**
 * Как форма обращается с почтой ученика.
 *
 * - `editable` — почту можно поменять (сам пользователь или админ);
 * - `readonly` — видна, но не меняется: у ученика собственный вход, а почта —
 *   это вход в аккаунт, её меняет только он сам или администратор;
 * - `noCabinet` — у ученика без кабинета почты нет: поле пустое и
 *   заблокировано, почта появится при создании кабинета.
 *
 * Отправляется почта только в режиме `editable` и только непустая.
 */
export type ContractFormEmailMode = 'editable' | 'readonly' | 'noCabinet'

/** Поля формы, под которые раскладываются ошибки 422 от API */
const SERVER_ERROR_FIELDS = [
  'surname',
  'first_name',
  'middle_name',
  'email',
  'birthday',
  'nickname',
  'contract_status',
  'contract_group_id',
  'contract_starts_at',
  'contract_expires_at',
  'contract_notes',
] as const satisfies ReadonlyArray<keyof UpdateUserData>

const createContractFormSchema = (t: (key: string) => string, emailMode: ContractFormEmailMode) => z.object({
  surname: z.string().max(255).optional(),
  first_name: z.string().max(255).optional(),
  middle_name: z.string().max(255).nullable().optional(),
  // Непредактируемую почту не проверяем и не отправляем: учитель не должен
  // упираться в проверку поля, которое не может поправить. Пустая почта
  // допустима — её просто не отправляем
  email: emailMode === 'editable'
    ? z.string().trim().email(t('users.invalidEmail')).max(255).or(z.string().trim().max(0)).nullable().optional()
    : z.string().nullable().optional(),
  birthday: z.string().nullable().optional(),
  nickname: z.string().max(255).nullable().optional(),
  contract_status: z
    .enum(['active', 'suspended', 'completed', 'cancelled'])
    .optional(),
  contract_group_id: z
    .number()
    .nullable()
    .optional(),
  contract_starts_at: z
    .string()
    .optional(),
  contract_expires_at: z
    .string()
    .nullable()
    .optional(),
  contract_notes: z
    .string()
    .max(1000, t('contracts.validation.notesMaxLength'))
    .nullable()
    .optional()
    .or(z.literal('')),
})

interface ContractFormProps {
  initialValues?: {
    surname?: string
    first_name?: string
    middle_name?: string | null
    email?: string
    birthday?: string | null
    nickname?: string | null
    contract_status?: ContractStatus
    contract_group_id?: number | null
    contract_starts_at?: string
    contract_expires_at?: string | null
    contract_notes?: string | null
  }
  /** Если true, показываются поля личных данных ученика (ФИО, email, день рождения, никнейм) */
  showUserFields?: boolean
  /** Поле почты среди личных данных; по умолчанию редактируемое */
  emailMode?: ContractFormEmailMode
  /** Ошибку, проброшенную из onSubmit, форма показывает сама: общим текстом и у полей */
  onSubmit: (data: UpdateUserData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  availableGroups?: Array<{ id: number; name: string }>
}

export function ContractForm({
  initialValues,
  showUserFields = false,
  emailMode = 'editable',
  onSubmit,
  onCancel,
  isLoading,
  availableGroups = [],
}: ContractFormProps) {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'
  const enums = useEnumsStore((state) => state.enums)
  const contractStatusOptions = enums?.ContractStatus ?? []
  const [submitError, setSubmitError] = useState<string | null>(null)

  const defaultContract = {
    contract_status: 'active' as const,
    contract_group_id: null as number | null,
    contract_starts_at: '',
    contract_expires_at: null as string | null,
    contract_notes: '',
  }
  const defaultUser = {
    surname: '',
    first_name: '',
    middle_name: '',
    email: '',
    birthday: '',
    nickname: '',
  }

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<UpdateUserData>({
    resolver: zodResolver(createContractFormSchema(t, emailMode)),
    defaultValues: {
      ...defaultUser,
      ...defaultContract,
      ...initialValues,
      middle_name: initialValues?.middle_name ?? '',
      nickname: initialValues?.nickname ?? '',
      birthday: initialValues?.birthday ?? '',
    },
  })

  const handleFormSubmit = async (data: UpdateUserData) => {
    let payload: UpdateUserData
    if (showUserFields) {
      // Почту отправляем, только если её можно менять и она заполнена:
      // пустое поле значит «почты нет», а не «стереть почту»
      const { email, ...rest } = data
      const trimmedEmail = email?.trim() ?? ''
      payload = emailMode === 'editable' && trimmedEmail !== ''
        ? { ...rest, email: trimmedEmail }
        : rest
    } else {
      payload = {
        contract_status: data.contract_status,
        contract_group_id: data.contract_group_id,
        contract_starts_at: data.contract_starts_at,
        contract_expires_at: data.contract_expires_at,
        contract_notes: data.contract_notes,
      }
    }

    setSubmitError(null)
    try {
      await onSubmit(payload)
    } catch (err: unknown) {
      const fieldErrors = getValidationErrors(err)
      for (const field of SERVER_ERROR_FIELDS) {
        const message = fieldErrors?.[field]?.[0]
        if (message && field in payload) {
          setError(field, { type: 'server', message })
        }
      }
      // Общий текст — всегда: ошибку у поля легко не заметить в длинной
      // форме, а ошибку без поля в форме иначе не увидеть вовсе
      setSubmitError(displayErrorMessage(getErrorMessage(err), t('users.updateError')))
    }
  }

  const isBusy = isLoading || isSubmitting

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {showUserFields && (
            <>
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.surname')}
                    error={!!errors.surname}
                    helperText={errors.surname?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.firstName')}
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="middle_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.middleName')}
                    error={!!errors.middle_name}
                    helperText={errors.middle_name?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="nickname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('users.nickname')}
                    error={!!errors.nickname}
                    helperText={errors.nickname?.message}
                    fullWidth
                    placeholder={t('users.nicknameHint')}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={emailMode === 'noCabinet' ? '' : (field.value ?? '')}
                    type="email"
                    label={t('common.email')}
                    disabled={emailMode !== 'editable'}
                    error={!!errors.email}
                    helperText={
                      errors.email?.message
                      ?? (emailMode === 'readonly'
                        ? t('users.emailChangeForbiddenHint')
                        : emailMode === 'noCabinet'
                          ? t('users.noCabinetEmailHint')
                          : undefined)
                    }
                    fullWidth
                  />
                )}
              />
              <Controller
                name="birthday"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label={t('auth.birthday')}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.birthday,
                        helperText: errors.birthday?.message,
                      },
                    }}
                  />
                )}
              />
            </>
          )}

          <Controller
            name="contract_status"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label={t('contracts.status')}
              error={!!errors.contract_status}
              helperText={errors.contract_status?.message}
              fullWidth
            >
              {contractStatusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {availableGroups.length > 0 && (
          <Controller
            name="contract_group_id"
            control={control}
            render={({ field }) => {
              const selectedGroup = field.value != null
                ? availableGroups.find((g) => g.id === field.value) ?? null
                : null
              return (
                <Autocomplete
                  options={availableGroups}
                  getOptionLabel={(opt) => opt.name}
                  value={selectedGroup}
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id ?? null)
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('contracts.group')}
                      error={!!errors.contract_group_id}
                      helperText={errors.contract_group_id?.message}
                      placeholder={t('contracts.noGroup')}
                    />
                  )}
                />
              )
            }}
          />
        )}

        <Controller
          name="contract_starts_at"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t('contracts.startsAt')}
              value={field.value ? dayjs(field.value) : null}
              onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.contract_starts_at,
                  helperText: errors.contract_starts_at?.message,
                },
              }}
            />
          )}
        />

        <Controller
          name="contract_expires_at"
          control={control}
          render={({ field }) => (
            <DatePicker
              label={t('contracts.expiresAt')}
              value={field.value ? dayjs(field.value) : null}
              onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : null)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.contract_expires_at,
                  helperText: errors.contract_expires_at?.message,
                },
              }}
            />
          )}
        />

        <Controller
          name="contract_notes"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('contracts.notes')}
              placeholder={t('contracts.notesPlaceholder')}
              error={!!errors.contract_notes}
              helperText={errors.contract_notes?.message}
              fullWidth
              multiline
              rows={4}
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isBusy}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isBusy}
          >
            {t('common.save')}
          </Button>
        </Box>
        </Stack>
      </form>
    </LocalizationProvider>
  )
}
